import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faPhone,
  faFire,
  faUser,
  faTimes,
  faChevronRight,
  faHeart,
  faCog,
  faQuestionCircle,
  faSignOutAlt,
  faSearch,
  faBars,
  faShoppingCart,
  faCartShopping,
} from "@fortawesome/free-solid-svg-icons";
import LoginModal from "../login/index";
import styles from "./navbar.module.css";
import { SERIES_DATA } from "../../data/furnitureData";
import { logout } from "../../service/auth";
import { useAuth } from "../../context/AuthContext";
import SearchBar from "../SearchBar/SearchBar";
import CartSidebar from "../CartSidebar";
import { useCart } from "../../context/CartContext";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const { isAuthenticated, user, logout: contextLogout } = useAuth();

  // Group series into categories
  const furnitureCategories = [
    {
      name: "Office Series",
      series: SERIES_DATA.filter((s) =>
        ["executive", "director", "visitor", "office-table"].includes(s.id)
      ),
    },
    {
      name: "Commercial Series",
      series: SERIES_DATA.filter((s) =>
        ["cafeteria", "student", "bar-stool", "hospital"].includes(s.id)
      ),
    },
    {
      name: "Specialty Series",
      series: SERIES_DATA.filter((s) =>
        ["cyber", "mesh", "dressing"].includes(s.id)
      ),
    },
  ];

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (!ticking) {
        requestAnimationFrame(() => {
          // Show/hide announcement bar based on scroll position
          if (currentScrollY > 100) {
            setShowAnnouncement(false);
          } else {
            setShowAnnouncement(true);
          }

          // Handle navbar background on scroll
          setIsScrolled(currentScrollY > 10);
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleClickOutside = (event: any) => {
      if (
        mobileMenuRef.current &&
        // @ts-ignore
        !mobileMenuRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
        setOpenMobileDropdown(null);
      }

      if (
        userDropdownRef.current &&
        // @ts-ignore
        !userDropdownRef.current.contains(event.target) &&
        showUserDropdown
      ) {
        setShowUserDropdown(false);
      }
    };

    const checkMobile = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
        setShowSearch(false);
      }
    };

    checkMobile();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", checkMobile);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkMobile);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  // Rest of your component remains the same...
  const handleDropdownEnter = (categoryName: any) => {
    if (!isMobile) setActiveDropdown(categoryName);
  };

  const handleDropdownLeave = () => {
    if (!isMobile) setActiveDropdown(null);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      setOpenMobileDropdown(null);
      setShowUserDropdown(false);
    }
  };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setOpenMobileDropdown(null);
    setShowUserDropdown(false);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      contextLogout();
      closeMobileMenu();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleMobileDropdown = (categoryName: any) => {
    setOpenMobileDropdown(
      openMobileDropdown === categoryName ? null : categoryName
    );
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
    closeMobileMenu();
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleUserAction = (action: any) => {
    action();
    setShowUserDropdown(false);
    closeMobileMenu();
  };

  // Render user dropdown for desktop
  const renderDesktopUserDropdown = () => {
    if (isAuthenticated) {
      return (
        <div className={styles.userDropdown} ref={userDropdownRef}>
          <div className={styles.userToggle} onClick={toggleUserDropdown}>
            <div className={styles.userAvatar}>
              {user?.picture?.path ? (
                <img src={user?.picture?.path} alt="user-dp" />
              ) : user?.name ? (
                user?.name?.charAt(0)
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </div>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`${styles.userDropdownChevron} ${
                showUserDropdown ? styles.rotated : ""
              }`}
            />
          </div>
          {showUserDropdown && (
            <div className={styles.userDropdownMenu}>
              {user?.role === "SUPER_ADMIN" && (
                <div
                  className={styles.dropdownItem}
                  onClick={() =>
                    handleUserAction(() => navigate("/admin-panel"))
                  }
                >
                  <FontAwesomeIcon icon={faCog} />
                  <span>Admin Panel</span>
                </div>
              )}
              <div
                className={styles.dropdownItem}
                onClick={() => handleUserAction(() => navigate("/profile"))}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>My Profile</span>
              </div>
              <div
                className={styles.dropdownItem}
                onClick={() => handleUserAction(() => navigate("/wishlist"))}
              >
                <FontAwesomeIcon icon={faHeart} />
                <span>My Wishlists</span>
              </div>
              <div
                className={styles.dropdownItem}
                onClick={() => handleUserAction(() => navigate("/cart"))}
              >
                <FontAwesomeIcon icon={faCartShopping} />
                <span>My Cart</span>
              </div>
              <div
                className={styles.dropdownItem}
                onClick={() => handleUserAction(() => navigate("/enquiries"))}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
                <span>My Enquiries</span>
              </div>

              <div
                onClick={() =>
                  handleUserAction(() =>
                    logout().finally(() => window.location.reload())
                  )
                }
                className={styles.dropdownItem}
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Sign Out</span>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <button
          onClick={handleLoginClick}
          className={styles.authButton}
          aria-label="Login or Register"
        >
          <FontAwesomeIcon icon={faUser} />
          <span>Login</span>
        </button>
      );
    }
  };

  // Render mobile user section
  const renderMobileUserSection = () => {
    if (isAuthenticated) {
      return (
        <div className={styles.mobileUserSection}>
          <div className={styles.mobileUserInfo}>
            <div className={styles.mobileUserAvatar}>
              {user?.name?.charAt(0) || <FontAwesomeIcon icon={faUser} />}
            </div>
            <div className={styles.mobileUserDetails}>
              <span className={styles.mobileUserName}>
                {user?.name || "My Account"}
              </span>
              <span className={styles.mobileUserEmail}>
                {user?.emailAddress}
              </span>
            </div>
          </div>
          <div className={styles.mobileUserActions}>
            {user?.role === "SUPER_ADMIN" && (
              <button
                className={styles.mobileUserAction}
                onClick={() => handleUserAction(() => navigate("/admin-panel"))}
              >
                <FontAwesomeIcon icon={faCog} />
                <span>Admin Panel</span>
              </button>
            )}
            <button
              className={styles.mobileUserAction}
              onClick={() => handleUserAction(() => navigate("/wishlist"))}
            >
              <FontAwesomeIcon icon={faHeart} />
              <span>Wishlists</span>
            </button>
            <button
              className={styles.mobileUserAction}
              onClick={() => handleUserAction(() => navigate("/enquiries"))}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>Enquiries</span>
            </button>
            <button className={styles.mobileUserAction} onClick={handleSignOut}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.mobileAuthSection}>
          <button
            onClick={handleLoginClick}
            className={styles.mobileLoginButton}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>Login / Register</span>
          </button>
          <p className={styles.authPrompt}>
            Access your account for a personalized experience
          </p>
        </div>
      );
    }
  };
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { cart, setCart, removeFromCart, updateQuantity, clearCart } =
    useCart();

  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCart(
      cart.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  return (
    <>
      <header
        className={`${styles.header} ${isScrolled ? styles.scrolled : ""}`}
      >
        {/* Top Announcement Bar */}
        <div
          className={`${styles.announcementBar} ${
            showAnnouncement ? styles.show : styles.hide
          }`}
        >
          <div className={styles.announcementContent}>
            <div className={styles.contactInfo}>
              <FontAwesomeIcon icon={faPhone} className={styles.contactIcon} />
              <span className={styles.contactText}>
                Need Assistance?{" "}
                <span className={styles.contactHours}>24/7 Support</span>
              </span>
            </div>

            <Link to="/contact" className={styles.specialOffer}>
              <FontAwesomeIcon icon={faFire} className={styles.fireIcon} />
              <span>Special Bulk Order Deals Available Now!</span>
            </Link>

            <div className={styles.topLinks}>
              <Link to="/contact" className={styles.topLink}>
                Enquire Now
              </Link>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <nav className={styles.navbar}>
          <div className={styles.navbarContainer}>
            {/* Logo */}
            <Link to="/" className={styles.logo} onClick={closeMobileMenu}>
              <img src="/images/scadellogosvg.png" alt="Scadel Furniture" />
            </Link>

            {/* Desktop Navigation */}
            <div className={styles.desktopNav}>
              <ul className={styles.navList}>
                <li className={styles.navItem}>
                  <Link to="/gallery" className={styles.navLink}>
                    Products
                  </Link>
                </li>

                {furnitureCategories.map((category) => (
                  <li
                    key={category.name}
                    className={styles.navItem}
                    onMouseEnter={() => handleDropdownEnter(category.name)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <div className={styles.navLink}>
                      {category.name}
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={styles.dropdownIcon}
                      />
                    </div>

                    {/* Dropdown Menu */}
                    <div
                      className={`${styles.dropdownMenu} ${
                        activeDropdown === category.name ? styles.visible : ""
                      }`}
                    >
                      <div className={styles.dropdownContent}>
                        <h4 className={styles.dropdownTitle}>
                          {category.name}
                        </h4>
                        <div className={styles.dropdownGrid}>
                          {category.series.map((series) => (
                            <Link
                              key={series.id}
                              to={`/series/${series.id}`}
                              className={styles.dropdownItem}
                            >
                              <div className={styles.seriesPreview}>
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/${series.folder}/${series.models[0].productId}.jpg`}
                                  alt={series.title}
                                  className={styles.seriesImage}
                                  loading="lazy"
                                />
                                <div className={styles.seriesInfo}>
                                  <h5 className={styles.seriesTitle}>
                                    {series.title}
                                  </h5>
                                  <p className={styles.seriesModels}>
                                    {series.models.length} models
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}

                <li className={styles.navItem}>
                  <Link to="/contact" className={styles.navLink}>
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Desktop Actions */}
            <div className={styles.desktopActions}>
              <div className={styles.searchContainer}>
                <SearchBar />
              </div>
              <div className={styles.actionIcons}>
                <button
                  className={styles.cartButton}
                  onClick={() => setIsCartOpen(true)}
                  aria-label="Shopping cart"
                >
                  <FontAwesomeIcon icon={faShoppingCart} />
                  {cart.length > 0 && (
                    <span className={styles.cartBadge}>{cart.length}</span>
                  )}
                </button>

                {renderDesktopUserDropdown()}
              </div>
            </div>

            {/* Mobile Actions */}
            <div className={styles.mobileActions}>
              <button
                className={styles.mobileActionButton}
                onClick={toggleSearch}
                aria-label="Search"
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>

              <button
                className={styles.cartButton}
                onClick={() => setIsCartOpen(true)}
                aria-label="Shopping cart"
              >
                <FontAwesomeIcon icon={faShoppingCart} />
                {cart.length > 0 && (
                  <span className={styles.cartBadge}>{cart.length}</span>
                )}
              </button>

              {isAuthenticated ? (
                <button
                  className={styles.mobileActionButton}
                  onClick={toggleUserDropdown}
                  aria-label="User account"
                >
                  <div className={styles.userAvatarSmall}>
                    {user?.name?.charAt(0) || <FontAwesomeIcon icon={faUser} />}
                  </div>
                </button>
              ) : (
                <button
                  className={styles.mobileActionButton}
                  onClick={handleLoginClick}
                  aria-label="Login"
                >
                  <FontAwesomeIcon icon={faUser} />
                </button>
              )}

              <button
                className={`${styles.mobileMenuToggle} ${
                  isMenuOpen ? styles.active : ""
                }`}
                onClick={toggleMenu}
                aria-label="Toggle menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          {showSearch && (
            <div className={styles.mobileSearch}>
              <SearchBar />
              <button
                className={styles.closeSearch}
                onClick={toggleSearch}
                aria-label="Close search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}
        </nav>

        {/* Mobile Navigation Menu */}
        <div
          className={`${styles.mobileMenu} ${isMenuOpen ? styles.active : ""}`}
          ref={mobileMenuRef}
        >
          <div className={styles.mobileMenuHeader}>
            <Link
              to="/"
              className={styles.mobileLogo}
              onClick={closeMobileMenu}
            >
              <img src="/images/scadellogosvg.png" alt="Scadel Furniture" />
            </Link>
            <button
              className={styles.closeMenuButton}
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className={styles.mobileMenuContent}>
            {/* User Section */}

            {renderMobileUserSection()}

            {/* Navigation Links */}
            <nav className={styles.mobileNav}>
              <div className={styles.mobileNavSection}>
                <h3 className={styles.mobileNavTitle}>Navigation</h3>
                <Link
                  to="/gallery"
                  className={styles.mobileNavLink}
                  onClick={closeMobileMenu}
                >
                  Products
                </Link>

                {furnitureCategories.map((category) => (
                  <div key={category.name} className={styles.mobileNavCategory}>
                    <button
                      className={styles.mobileNavLink}
                      onClick={() => toggleMobileDropdown(category.name)}
                    >
                      {category.name}
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className={`${styles.dropdownIcon} ${
                          openMobileDropdown === category.name
                            ? styles.rotated
                            : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`${styles.mobileDropdown} ${
                        openMobileDropdown === category.name
                          ? styles.active
                          : ""
                      }`}
                    >
                      {category.series.map((series) => (
                        <Link
                          key={series.id}
                          to={`/series/${series.id}`}
                          className={styles.mobileDropdownItem}
                          onClick={closeMobileMenu}
                        >
                          <div className={styles.seriesPreview}>
                            <img
                              src={`${process.env.PUBLIC_URL}/images/${series.folder}/${series.models[0].productId}.jpg`}
                              alt={series.title}
                              className={styles.seriesImage}
                            />
                            <span>{series.title}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}

                <Link
                  to="/contact"
                  className={styles.mobileNavLink}
                  onClick={closeMobileMenu}
                >
                  Contact
                </Link>
              </div>
            </nav>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className={styles.mobileOverlay} onClick={closeMobileMenu}></div>
        )}

        {/* Login Modal */}
        {showLoginModal && (
          <LoginModal
            isStandalone={false}
            onClose={() => setShowLoginModal(false)}
            show={showLoginModal}
          />
        )}
      </header>

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
      />
    </>
  );
};

export default Navbar;
